/**
 * Terrain rasteriser.
 *
 * The map is drawn once into a single texture at 4x4 art pixels per tile, with
 * a per-tile hashed motif (waves, grass tufts, trees, peaks, crystals) so that
 * nothing tiles visibly.
 */

import { Texture } from 'pixi.js';
import { ART, MAP_H, MAP_W, Terrain } from '../../shared/constants';
import { hash2 } from '../../shared/util';

const BASE: Record<number, [number, number, number]> = {
  [Terrain.Ocean]: [0x14, 0x2c, 0x4a],
  [Terrain.Shallow]: [0x24, 0x5f, 0x82],
  [Terrain.Plains]: [0x59, 0x8a, 0x3d],
  [Terrain.Forest]: [0x2c, 0x54, 0x2b],
  [Terrain.Mountain]: [0x6b, 0x67, 0x60],
  [Terrain.Ley]: [0x45, 0x36, 0x63],
  // High Peaks: paler and colder than walkable mountain, so a range's
  // impassable spine is obvious at any zoom without needing a legend
  [Terrain.Peak]: [0xb4, 0xb8, 0xc4],
};

const ACCENT: Record<number, [number, number, number]> = {
  [Terrain.Ocean]: [0x1d, 0x3e, 0x63],
  [Terrain.Shallow]: [0x39, 0x86, 0xad],
  [Terrain.Plains]: [0x6d, 0xa3, 0x4b],
  [Terrain.Forest]: [0x1f, 0x3d, 0x1f],
  [Terrain.Mountain]: [0x9d, 0x97, 0x8b],
  [Terrain.Ley]: [0xc2, 0x9a, 0xff],
  [Terrain.Peak]: [0xf2, 0xf6, 0xff],
};

/** One piece of the painted world, and where it belongs in world pixels. */
export interface TerrainChunk {
  texture: Texture;
  x: number;
  y: number;
}

export interface TerrainRender {
  chunks: TerrainChunk[];
}

/**
 * **Why the map is painted in pieces.**
 *
 * At `ART = 8` a full 672 × 420 world is 5376 × 3360 pixels. WebGL only
 * guarantees a maximum texture dimension of 2048, and 4096 is the real-world
 * ceiling on a great many integrated and mobile GPUs — so a single texture that
 * size does not merely run slowly on those machines, it **fails to upload and
 * the terrain does not appear at all**. That is not a performance trade-off, it
 * is a blank screen for a large share of players.
 *
 * 256 tiles a side keeps every chunk at 2048 × 2048, which is inside the
 * guaranteed minimum and therefore safe everywhere. A full world becomes nine
 * chunks; a small one stays a single chunk exactly as before.
 */
const CHUNK_TILES = 256;

/**
 * Paints the terrain once, at match start.
 *
 * Only the *world* is painted, not the whole grid: a small match rasterises a
 * proportionally smaller texture and keeps a proportionally smaller one in
 * video memory (a 20-kingdom world is 6 MB against 18). `MAP_W` remains the
 * stride of the terrain array either way — it is the grid that is fixed, not
 * the world drawn inside it.
 */
export function buildTerrain(
  terrain: Uint8Array, seed: number, worldW = MAP_W, worldH = MAP_H,
): TerrainRender {
  const TW = Math.max(1, Math.min(MAP_W, worldW));
  const TH = Math.max(1, Math.min(MAP_H, worldH));

  const chunks: TerrainChunk[] = [];

  // one pass per chunk; the tile loop inside is exactly what it always was,
  // only offset so it paints into the chunk's own little canvas
  for (let cy0 = 0; cy0 < TH; cy0 += CHUNK_TILES) {
    for (let cx0 = 0; cx0 < TW; cx0 += CHUNK_TILES) {
      const cw = Math.min(CHUNK_TILES, TW - cx0);
      const ch = Math.min(CHUNK_TILES, TH - cy0);
      const W = cw * ART;
      const H = ch * ART;

      const canvas = document.createElement('canvas');
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext('2d')!;
      const img = ctx.createImageData(W, H);
      const data = img.data;

      const put = (x: number, y: number, r: number, g: number, b: number) => {
        const o = (y * W + x) * 4;
        data[o] = r; data[o + 1] = g; data[o + 2] = b; data[o + 3] = 255;
      };

      paintChunk(terrain, seed, cx0, cy0, cw, ch, put);

      ctx.putImageData(img, 0, 0);
      const texture = Texture.from(canvas);
      try { (texture.source as any).scaleMode = 'nearest'; } catch { /* ignore */ }
      chunks.push({ texture, x: cx0 * ART, y: cy0 * ART });
    }
  }

  return { chunks };
}

/** Paints one chunk's tiles into its own little canvas. */
function paintChunk(
  terrain: Uint8Array, seed: number,
  cx0: number, cy0: number, cw: number, ch: number,
  put: (x: number, y: number, r: number, g: number, b: number) => void,
) {
  for (let iy = 0; iy < ch; iy++) {
    for (let ix = 0; ix < cw; ix++) {
      const tx = cx0 + ix, ty = cy0 + iy;
      const t = terrain[ty * MAP_W + tx];
      const v = hash2(tx, ty, seed + 4242);
      const base = BASE[t] || BASE[Terrain.Plains];
      const acc = ACCENT[t] || ACCENT[Terrain.Plains];
      const shade = 0.90 + v * 0.18;

      const br = Math.min(255, base[0] * shade) | 0;
      const bg = Math.min(255, base[1] * shade) | 0;
      const bb = Math.min(255, base[2] * shade) | 0;

      const ox = ix * ART, oy = iy * ART;
      for (let y = 0; y < ART; y++) {
        for (let x = 0; x < ART; x++) put(ox + x, oy + y, br, bg, bb);
      }

      /*
       * Per-terrain motif, drawn in the 8 × 8 box a tile now owns.
       *
       * Four times the pixels of the old 4 × 4, and spent on things that were
       * simply not expressible before: a wave has a crest and a trough, a tree
       * has a trunk you can see under a canopy, a mountain has a lit face and a
       * shadowed one. `v` is the tile's own hash, so every motif is placed and
       * varied deterministically and nothing tiles visibly.
       */
      const dark = (f: number) => [(br * f) | 0, (bg * f) | 0, (bb * f) | 0] as const;
      switch (t) {
        case Terrain.Ocean: {
          // long swells, only on some tiles, so open water stays calm
          if (v > 0.72) {
            const wy = 2 + ((v * 4) | 0);
            for (let x = 1; x < 7; x++) {
              put(ox + x, oy + wy + (x === 3 || x === 4 ? -1 : 0), acc[0], acc[1], acc[2]);
            }
          }
          break;
        }
        case Terrain.Shallow: {
          // ripples: two short broken lines offset from each other
          const r1 = 1 + ((v * 3) | 0);
          put(ox + 1, oy + r1, acc[0], acc[1], acc[2]);
          put(ox + 2, oy + r1, acc[0], acc[1], acc[2]);
          put(ox + 4, oy + r1 + 1, acc[0], acc[1], acc[2]);
          put(ox + 5, oy + r1 + 1, acc[0], acc[1], acc[2]);
          put(ox + 3, oy + r1 + 3, acc[0], acc[1], acc[2]);
          put(ox + 6, oy + r1 + 3, acc[0], acc[1], acc[2]);
          break;
        }
        case Terrain.Plains: {
          // tufts of grass, and the odd bare patch of earth
          const gx = 1 + ((v * 4) | 0);
          put(ox + gx, oy + 5, acc[0], acc[1], acc[2]);
          put(ox + gx, oy + 4, acc[0], acc[1], acc[2]);
          put(ox + gx + 1, oy + 5, acc[0], acc[1], acc[2]);
          if (v > 0.55) {
            put(ox + gx + 3, oy + 3, acc[0], acc[1], acc[2]);
            put(ox + gx + 3, oy + 2, acc[0], acc[1], acc[2]);
          }
          if (v < 0.18) {
            const d = dark(0.86);
            put(ox + 5, oy + 6, d[0], d[1], d[2]);
            put(ox + 6, oy + 6, d[0], d[1], d[2]);
            put(ox + 5, oy + 7, d[0], d[1], d[2]);
          }
          break;
        }
        case Terrain.Forest: {
          // a proper little tree: layered canopy over a visible trunk
          const cx = 1 + ((v * 3) | 0);
          put(ox + cx + 1, oy + 0, 0x4a, 0x86, 0x40);
          put(ox + cx, oy + 1, 0x4a, 0x86, 0x40);
          put(ox + cx + 1, oy + 1, 0x3f, 0x77, 0x38);
          put(ox + cx + 2, oy + 1, 0x36, 0x66, 0x30);
          put(ox + cx - 1, oy + 2, 0x3f, 0x77, 0x38);
          put(ox + cx, oy + 2, 0x36, 0x66, 0x30);
          put(ox + cx + 1, oy + 2, 0x36, 0x66, 0x30);
          put(ox + cx + 2, oy + 2, 0x2a, 0x50, 0x27);
          put(ox + cx, oy + 3, 0x2a, 0x50, 0x27);
          put(ox + cx + 1, oy + 3, 0x2a, 0x50, 0x27);
          put(ox + cx + 1, oy + 4, 0x4a, 0x33, 0x22);
          put(ox + cx + 1, oy + 5, 0x3a, 0x28, 0x1a);
          // a second, smaller tree on some tiles so woodland reads as woodland
          if (v > 0.62) {
            put(ox + 5, oy + 5, 0x3f, 0x77, 0x38);
            put(ox + 6, oy + 5, 0x36, 0x66, 0x30);
            put(ox + 5, oy + 6, 0x2a, 0x50, 0x27);
            put(ox + 6, oy + 6, 0x4a, 0x33, 0x22);
          }
          break;
        }
        case Terrain.Mountain: {
          // a ridge with a lit western face and a shadowed eastern one
          const px = 2 + ((v * 2) | 0);
          put(ox + px, oy + 1, 0xb6, 0xb0, 0xa4);
          put(ox + px - 1, oy + 2, 0xb6, 0xb0, 0xa4);
          put(ox + px, oy + 2, acc[0], acc[1], acc[2]);
          put(ox + px + 1, oy + 2, 0x5e, 0x5a, 0x54);
          put(ox + px - 2, oy + 3, acc[0], acc[1], acc[2]);
          put(ox + px - 1, oy + 3, acc[0], acc[1], acc[2]);
          put(ox + px, oy + 3, 0x6b, 0x67, 0x60);
          put(ox + px + 1, oy + 3, 0x4a, 0x47, 0x42);
          put(ox + px + 2, oy + 3, 0x3f, 0x3c, 0x38);
          put(ox + px - 2, oy + 4, 0x6b, 0x67, 0x60);
          put(ox + px + 1, oy + 4, 0x3f, 0x3c, 0x38);
          put(ox + 1, oy + 6, 0x4a, 0x47, 0x42);
          put(ox + 6, oy + 6, 0x4a, 0x47, 0x42);
          break;
        }
        case Terrain.Ley: {
          // a cluster of crystals with a lit core
          put(ox + 3, oy + 1, acc[0], acc[1], acc[2]);
          put(ox + 3, oy + 2, 0xe6, 0xd4, 0xff);
          put(ox + 4, oy + 2, acc[0], acc[1], acc[2]);
          put(ox + 2, oy + 3, acc[0], acc[1], acc[2]);
          put(ox + 3, oy + 3, 0xe6, 0xd4, 0xff);
          put(ox + 4, oy + 3, acc[0], acc[1], acc[2]);
          put(ox + 3, oy + 4, acc[0], acc[1], acc[2]);
          if (v > 0.5) {
            put(ox + 6, oy + 4, acc[0], acc[1], acc[2]);
            put(ox + 6, oy + 5, 0xe6, 0xd4, 0xff);
            put(ox + 1, oy + 5, acc[0], acc[1], acc[2]);
          }
          break;
        }
        /*
         * A snow-capped crag, drawn taller and brighter than a walkable
         * mountain. Nothing crosses these, so they have to be *unmistakable* at
         * the pulled-back view a player picks a homeland from — reading a range
         * as ordinary hills and marching into a dead end is the one mistake
         * this terrain must never invite. Eight pixels buys a real summit with
         * a snowline, which four never could.
         */
        case Terrain.Peak: {
          put(ox + 4, oy + 0, 0xff, 0xff, 0xff);
          put(ox + 3, oy + 1, 0xff, 0xff, 0xff);
          put(ox + 4, oy + 1, 0xff, 0xff, 0xff);
          put(ox + 5, oy + 1, 0xe4, 0xea, 0xf6);
          put(ox + 2, oy + 2, 0xff, 0xff, 0xff);
          put(ox + 3, oy + 2, 0xe4, 0xea, 0xf6);
          put(ox + 4, oy + 2, 0xe4, 0xea, 0xf6);
          put(ox + 5, oy + 2, 0xc2, 0xcb, 0xdb);
          put(ox + 6, oy + 2, 0xa8, 0xaf, 0xbe);
          // the snowline: below it the rock is bare
          put(ox + 1, oy + 3, 0xa8, 0xaf, 0xbe);
          put(ox + 2, oy + 3, 0x9a, 0xa2, 0xb2);
          put(ox + 3, oy + 3, 0x8e, 0x95, 0xa6);
          put(ox + 4, oy + 3, 0x8e, 0x95, 0xa6);
          put(ox + 5, oy + 3, 0x7c, 0x83, 0x93);
          put(ox + 6, oy + 3, 0x6c, 0x73, 0x82);
          put(ox + 0, oy + 4, 0x8e, 0x95, 0xa6);
          put(ox + 1, oy + 4, 0x7c, 0x83, 0x93);
          put(ox + 3, oy + 4, 0x6c, 0x73, 0x82);
          put(ox + 5, oy + 4, 0x5e, 0x64, 0x72);
          put(ox + 7, oy + 4, 0x5e, 0x64, 0x72);
          put(ox + 0, oy + 5, 0x6c, 0x73, 0x82);
          put(ox + 2, oy + 5, 0x5e, 0x64, 0x72);
          put(ox + 4, oy + 5, 0x55, 0x5b, 0x68);
          put(ox + 6, oy + 5, 0x5e, 0x64, 0x72);
          put(ox + 1, oy + 6, 0x55, 0x5b, 0x68);
          put(ox + 5, oy + 6, 0x4b, 0x50, 0x5c);
          break;
        }
      }
    }
  }
}
